/**
 * The mapping from a NetSuite product onto the design system's presentational props,
 * shared by the grid card, the list row and the quick look.
 *
 * These three used to hold their own copies, which is how the same item ends up reading
 * "In stock" in one view and "Low stock" in another.
 */

/**
 * The app has no low-stock concept to inherit — `totalquantityonhand` is only ever
 * compared against 0 — and NetSuite's `reorderpoint`, `preferredstocklevel` and
 * `safetystocklevel` are **NULL on all 11,093 live items**, so there is no business
 * threshold to borrow either. This number is a guess; change it here.
 *
 * For calibration, of the ~4,500 items with stock on hand: 1,809 have 1-2, 971 have 3-5,
 * 694 have 6-10, and only 1,033 have more than 10. At 10, most in-stock items read
 * "Low stock".
 */
export const LOW_STOCK_THRESHOLD = 10;

/**
 * `out` renders as **"Backorder"**, which is accurate here rather than a euphemism — this
 * business takes orders for items it doesn't hold. Note ~27% of live items have
 * `totalquantityonhand` NULL rather than 0, i.e. "unknown" rather than "none", and this
 * currently treats both as backorder.
 */
export function stockState(product) {
  const onHand = Number(product?.totalquantityonhand) || 0;
  if (onHand > LOW_STOCK_THRESHOLD) return "in";
  if (onHand > 0) return "low";
  return "out";
}

/**
 * The DS badge slot holds one pill, so these are ranked by commercial weight.
 * `stockdescription` is not a pack size — `calculateActualQuantity` parses it as a
 * buy-X-get-Y promotion, so it's the free goods the customer is owed.
 *
 * The Sale badge is separate and automatic, driven by `originalPrice`.
 */
export function badgeFor(product) {
  if (product?.stockdescription) return product.stockdescription;
  if (product?.pricing && product.pricing.length > 1) {
    return "Quantity Discounts";
  }
  return undefined;
}

/** `"/ Pack of 10"`, or undefined for the ~34 items with no Primary Sale Unit. */
export function unitLabel(product) {
  return product?.sale_unit ? `/ ${product.sale_unit}` : undefined;
}

/**
 * `storedetaileddescription` is HTML — the PDP renders it through `ShowMoreHtml` with
 * `dangerouslySetInnerHTML`. The design system's quick look renders `description` as
 * plain text, so tags would show through verbatim. Strip them here rather than teaching
 * the DS about NetSuite markup or handing it raw HTML to inject.
 *
 * Only on the listing's by-id payload; `_getStandardFields()` doesn't carry this field.
 */
export function plainDescription(product, maxLength = 260) {
  const html = product?.storedetaileddescription;
  if (!html) return undefined;
  const text = String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return undefined;
  return text.length > maxLength
    ? `${text.slice(0, maxLength).trimEnd()}…`
    : text;
}
